<template>
    <div>
        <simple-modal :show="showModal" @close="closeModal"
                      :title="section === 0 ? 'Link existing contact' : 'Create new contact'">
            <div>
                <div v-if="section===0">
                    <!-- Search Contact -->
                    <div class="my-2">
                        <label for="searchContact" class="font-medium">Search Contact</label>
                        <div class="">
                            <el-select
                                value-key="id"
                                class="w-full"
                                v-model="selectedContact"
                                @focus="searchContacts('')"
                                filterable
                                remote
                                clearable
                                :placeholder="'Search name, email or mobile'"
                                :remote-method="searchContacts"
                                :loading="loading">
                                <el-option
                                    v-for="contact in suggestedContacts"
                                    :key="contact.id"
                                    :label="contact.full_name"
                                    :value="contact">
                                    <div v-if="contact" class="relative flex items-center gap-2">
                                        <div class="font-normal text-gray-900 text-sm">{{ contact.full_name }}
                                            <span v-if="contact.juristic_name" class="text-indigo-600 font-light leading-tight text-xs">(for {{
                                                    contact.juristic_name
                                                }})</span></div>
                                        <div v-if="contact.external_source !== null"
                                             class="text-gray-400 text-xs -mt-3 flex items-center gap-1">
                                            <icon-link class="w-3 h-3"></icon-link>
                                            {{ contact.external_source }}
                                        </div>
                                    </div>
                                </el-option>
                            </el-select>
                        </div>
                    </div>

                    <div class="my-6 p-4 border-gray-200 bg-gray-50 rounded-md" v-if="selectedContact">
                        <div class="w-full">
                            <h4 class="flex justify-between">
                                <span class="text-base text-indigo-600">{{ selectedContact.full_name }}</span>
                                <span v-if="selectedContact.designation" class="bg-indigo-100 text-indigo-600 px-2 font-medium text-xs rounded">
                                    {{ selectedContact.designation }}
                                </span>
                            </h4>
                            <h4 v-if="selectedContact.juristic_name" class="mb-1 text-xs font-medium text-indigo-600">
                                Representing {{ selectedContact.juristic_name }}
                            </h4>
                            <div class="ml-3">
                                <div class="flex items-center" v-if="selectedContact.email !== null">
                                    <icon-email class="w-3 h-3 mr-2"></icon-email>
                                    {{ selectedContact.email }}
                                </div>
                                <div class="flex items-center" v-if="selectedContact.mobile !== null">
                                    <icon-device-mobile class="w-3 h-3 mr-2"></icon-device-mobile>
                                    {{ selectedContact.mobile }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col items-center text-center my-6">
                        <div class="my-2">OR</div>
                        <button href="#" class="text-indigo-600" @click.prevent="showCreateSection">
                            Click here to create a new contact
                        </button>
                    </div>

                </div>

                <div v-if="section===1">
                    <!--START Contact Form Fields-->
                    <div class="mt-2 grid grid-cols-1 gap-y-2 md:grid-cols-6 md:gap-x-2">

                        <!--Title-->
                        <div class="col-span-6 md:col-span-2">
                            <label class="select-none font-medium">Title</label>
                            <el-select
                                id="title"
                                v-model="form.title"
                                placeholder=""
                                class="w-full">
                                <el-option key="none"
                                           label="None"
                                           :value=null>
                                </el-option>
                                <el-option
                                    v-for="contactTitle in titleOptions"
                                    :key="contactTitle"
                                    :label="contactTitle"
                                    :value="contactTitle">
                                </el-option>
                            </el-select>
                        </div>

                        <!--First Names-->
                        <div class="col-span-6 md:col-span-4">
                            <label class="select-none font-medium">First Names</label>
                            <input v-model="form.first_names"
                                   @blur="setContactName"
                                   class="form-control"
                                   type="text"
                                   id="first_names"
                                   name="first_names"
                                   :class="{'is-invalid': form.errors.has('first_names')}">

                            <span class="invalid-feedback" v-show="form.errors.has('first_names')">
                                {{ form.errors.get("first_names") }}
                            </span>
                        </div>

                        <!--Last Name-->
                        <div class="col-span-6 md:col-span-4">
                            <label class="select-none font-medium">Last Name</label>
                            <input v-model="form.last_name"
                                   class="form-control"
                                   type="text"
                                   id="last_name"
                                   name="last_name"
                                   :class="{'is-invalid': form.errors.has('last_name')}">

                            <span class="invalid-feedback" v-show="form.errors.has('last_name')">
                                {{ form.errors.get("last_name") }}
                            </span>
                        </div>

                        <!--Designation-->
                        <div class="col-span-6 md:col-span-2">
                            <label class="select-none font-medium">Designation</label>
                            <input v-model.trim="form.designation"
                                   placeholder="eg. Accountant"
                                   class="form-control"
                                   id="designation"
                                   @blur="formatDesignation"
                                   name="designation"
                                   :class="{'is-invalid': form.errors.has('designation')}">

                            <span class="invalid-feedback"
                                  v-show="form.errors.has('designation')">{{ form.errors.get("designation") }}
                            </span>
                        </div>

                        <!--Email Address-->
                        <div class="col-span-6 md:col-span-4">
                            <label class="select-none font-medium">Email</label>
                            <input v-model="form.email"
                                   class="form-control"
                                   type="email"
                                   id="email"
                                   name="email"
                                   :class="{'is-invalid': form.errors.has('email')}">

                            <span class="invalid-feedback"
                                  v-show="form.errors.has('email')">{{ form.errors.get("email") }}
                            </span>
                        </div>

                        <!--Contact Name-->
                        <div class="col-span-6 md:col-span-2">
                            <label class="select-none font-medium">Contact Name</label>
                            <input v-model="form.contact_name"
                                   class="form-control"
                                   value="form.contact_name"
                                   type="text"
                                   id="contact_name"
                                   name="contact_name"
                                   :class="{'is-invalid': form.errors.has('contact_name')}">
                            <span class="invalid-feedback" v-show="form.errors.has('contact_name')">
                                {{ form.errors.get("contact_name") }}
                            </span>
                        </div>

                        <!--Other Emails-->
                        <div class="col-span-6 md:col-span-4">
                            <label class="select-none font-medium">Other Emails <span class="small">(Separate with commas)</span></label>
                            <div class="flex items-center gap-2">
                                <input v-model="form.email_other"
                                       class="form-control w-full"
                                       type="text"
                                       id="email_other"
                                       name="email_other"
                                       :class="{'is-invalid': form.errors.has('email_other')}">
                            </div>
                            <span class="invalid-feedback" v-show="form.errors.has('email_other')">
                                {{ form.errors.get("email_other") }}
                            </span>
                        </div>

                        <!--Always CC Other checkbox-->
                        <div class="col-span-6 md:col-span-2 md:mt-6">
                            <label class="select-none hidden md:block"></label>
                            <checkbox class="whitespace-nowrap font-medium select-none" v-model="form.always_cc_other"><span class="ml-2">Always CC</span></checkbox>
                        </div>

                        <!--Mobile-->
                        <div class="col-span-6 md:col-span-4">
                            <label class="select-none font-medium">Mobile</label>
                            <input v-model="form.mobile"
                                   class="form-control"
                                   type="text"
                                   id="mobile"
                                   name="mobile"
                                   :class="{'is-invalid': form.errors.has('mobile')}">

                            <span class="invalid-feedback" v-show="form.errors.has('mobile')">
                                {{ form.errors.get("mobile") }}
                            </span>
                        </div>

                        <!--Phone-->
                        <div class="col-span-6 md:col-span-2">
                            <label class="select-none font-medium">Phone</label>
                            <input v-model="form.phone"
                                   class="form-control"
                                   type="text"
                                   id="phone"
                                   name="phone"
                                   :class="{'is-invalid': form.errors.has('phone')}">

                            <span class="invalid-feedback" v-show="form.errors.has('phone')">
                                {{ form.errors.get("phone") }}
                            </span>
                        </div>

                        <!--Represents Juristic Entity checkbox-->
                        <div class="mt-4 col-span-6">
                            <checkbox class="whitespace-nowrap font-medium flex" v-model="form.represents_juristic_entity">
                                <span class="ml-2 flex items-center select-none gap-x-1">
                                    Represents<span class="font-bold">{{ form.juristic_type === "Other Juristic Entity" ? " an " : " a " }} </span>
                                    <span class="font-bold">{{ form.juristic_type ? form.juristic_type : "juristic entity" }}</span>
                                    <span class="ml-2"
                                          :title="'If the contact is representing a juristic entity,select this option to capture the entity details'">
                                        <icon-help class="h-3 w-3"></icon-help>
                                    </span>
                                </span>
                            </checkbox>
                        </div>

                        <!--Juristic Entity Section-->
                        <div v-if="form.represents_juristic_entity == true" class="col-span-6 md:col-span-6 grid grid-cols-6 gap-2">

                            <!--Entity Name-->
                            <div class="col-span-6 md:col-span-4">
                                <label class="select-none font-medium">Entity Name</label>
                                <input v-model="form.juristic_name"
                                       class="form-control"
                                       type="text"
                                       id="juristic_name"
                                       name="juristic_name"
                                       :class="{'is-invalid': form.errors.has('juristic_name')}">

                                <span class="invalid-feedback"
                                      v-show="form.errors.has('juristic_name')">
                                    {{ form.errors.get("juristic_name") }}
                                </span>
                            </div>

                            <!--Entity Type-->
                            <div class="col-span-6 md:col-span-2">
                                <label class="select-none font-medium">Entity Type</label>
                                <el-select
                                    id="juristic_type"
                                    class="w-full"
                                    v-model="form.juristic_type">
                                    <el-option key="none"
                                               label="None"
                                               value="">
                                    </el-option>
                                    <el-option
                                        v-for="entityType in juristicEntityTypes"
                                        :key="entityType"
                                        :label="entityType"
                                        :value="entityType">
                                    </el-option>
                                </el-select>
                            </div>

                            <!--Registration Nr-->
                            <div class="col-span-6 md:col-span-3">
                                <label class="select-none font-medium">Registration Nr.</label>
                                <input v-model="form.registration_number"
                                       class="form-control"
                                       type="text"
                                       id="registration_number"
                                       name="registration_number"
                                       :class="{'is-invalid': form.errors.has('registration_number')}">

                                <span class="invalid-feedback"
                                      v-show="form.errors.has('registration_number')">
                                    {{ form.errors.get("registration_number") }}
                                </span>
                            </div>

                            <!--Vat Number-->
                            <div class="col-span-6 md:col-span-3">
                                <label class="select-none font-medium">Vat Nr.</label>
                                <input v-model="form.vat_number"
                                       class="form-control"
                                       type="text"
                                       id="vat_number"
                                       name="vat_number"
                                       :class="{'is-invalid': form.errors.has('vat_number')}">

                                <span class="invalid-feedback" v-show="form.errors.has('vat_number')">
                                    {{ form.errors.get("vat_number") }}
                                </span>
                            </div>

                            <!--Website-->
                            <div class="col-span-6 md:col-span-6">
                                <label class="select-none font-medium">Website</label>
                                <input v-model="form.website"
                                       placeholder="eg. https://mywebsite.com"
                                       class="form-control"
                                       type="url"
                                       id="website"
                                       name="website"
                                       :class="{'is-invalid': form.errors.has('website')}">

                                <span class="invalid-feedback" v-show="form.errors.has('website')">
                                    {{ form.errors.get("website") }}
                                </span>
                            </div>
                        </div>
                        <!--End Juristic Section-->

                        <!--Prevent External Updates checkbox-->
                        <div class="mt-4 col-span-6">
                            <label class="select-none font-medium">
                                <checkbox class="font-medium flex" v-model="form.prevent_external_updates">
                                    <span class="ml-2 flex items-center select-none" title="">Prevent integrations from updating this record
                                        <span class="ml-2"
                                              title="Prevent data syncing from external systems such as rams or payprop from overriding changes you make to this record.">
                                            <icon-help class="h-3 w-3"></icon-help>
                                        </span>
                                    </span>
                                </checkbox>
                            </label>
                        </div>
                    </div>
                    <!--END Contact Form Fields-->
                </div>

                <!--Form Navigation Buttons-->
                <div class="mt-6 flex justify-between items-center">
                    <button
                        v-if="section === 0"
                        type="button"
                        @click="closeModal"
                        class="select-none px-4 py-2 border border-gray-300 text-gray-700 shadow-sm hover:text-gray-500 text-sm font-medium rounded-md shadow text-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-400 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                        data-dismiss="modal"
                        :disabled="form.busy">
                        Cancel
                    </button>

                    <button
                        v-if="selectedContact && section === 0"
                        class="select-none px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                        @click.prevent="linkExistingContact"
                        :disabled="form.busy">
                        {{ form.busy ? "Linking Contact..." : "Link" }}
                    </button>

                    <button
                        v-if="section === 1"
                        type="button"
                        @click="section = 0"
                        class="select-none px-4 py-2 border border-gray-300 text-gray-700 shadow-sm hover:text-gray-500 text-sm font-medium rounded-md shadow text-white hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-400 transition ease-in-out duration-150 sm:text-sm sm:leading-5"
                        data-dismiss="modal"
                        :disabled="form.busy">
                        Back
                    </button>

                    <button
                        v-if="section === 1"
                        class="select-none px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow text-white bg-indigo-600 hover:bg-indigo-700"
                        @click.prevent="createAndLinkContact"
                        :disabled="form.busy">
                        {{ form.busy ? "Creating Contact..." : "Create Contact" }}
                    </button>
                </div>

            </div>
        </simple-modal>
    </div>
</template>

<script>
import ContactFormListsMixin from "../../mixins/contact-form-lists-mixin";

export default {
    name: "CreateOrLinkContactModal",
    props: ["showModal", "role", "contactable", "contactableId"],
    mixins: [ContactFormListsMixin],
    data: () => {
        return {
            section: 0,
            selectedContact: null,
            suggestedContacts: [],
            loading: false,
            form: new AppForm({})
        };
    },
    created () {
        this.initialiseFrom();
    },
    methods: {
        searchContacts (query) {
            this.loading = true;
            axios.get("/contacts/search-to-link", {
                params: {
                    filter_text: query
                }
            }).then((response) => {
                this.suggestedContacts = response.data;
                this.loading = false;
            }).catch((errors) => {
                toastr.error("Could not find any matching contacts!");
                this.loading = false;
            });
        },
        createAndLinkContact () {
            this.form.contactable = this.contactable;
            this.form.contactable_id = this.contactableId;
            this.form.role = this.role;

            App.post("/contacts/create", this.form)
            .then((response) => {
                this.$emit("contact-created", response);
                toastr.success("New contact created and linked to asset");
                this.initialiseFrom();
                this.$emit("close");
            })
            .catch((errors) => {
                toastr.error("Creating contact failed!");
            });
        },
         linkExistingContact () {
                this.form.role = this.role;
                App.post("/contacts/" + this.selectedContact.id + "/attach", new AppForm({
                    contactable: this.contactable,
                    contactable_id: this.contactableId,
                    role: this.role
                })).then(() => {
                    this.selectedContact = null;
                    this.search = null;
                    this.$emit('contact-linked');
                    this.closeModal();
                    toastr.success("Contact Linked Successfully!");
                }).catch(() => {
                    toastr.error("Could not link contact!");
                });
            },
        showCreateSection () {
            this.selectedContact = null;
            this.section = 1;
        },
        closeModal () {
            this.initialiseFrom();
            this.$emit("close");
        },
        setContactName () {
            if (this.form.first_names && !this.contactNameSet && !this.form.contact_name) {
                let fullFirstNames = this.form.first_names;
                this.form.contact_name = fullFirstNames.replace(/ .*/, "");
                this.contactNameSet = false;
            }
        },
        formatDesignation () {
            if (this.form.designation) {
                this.form.designation = this.$options.filters.capitalize(this.form.designation);
            }
        },
        initialiseFrom () {
            this.form = new AppForm({
                title: null,
                first_names: null,
                last_name: null,
                contact_name: null,
                designation: null,
                email: null,
                email_other: null,
                always_cc_other: false,
                mobile: null,
                phone: null,
                represents_juristic_entity: false,
                juristic_name: null,
                juristic_type: null,
                registration_number: null,
                vat_number: null,
                website: null,
                prevent_external_updates: false,
            });

            this.selectedContact = null;
            this.section = 0;
        },
    }
};
</script>
